import { useDroppable } from "@dnd-kit/core";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import {
  Box,
  Breadcrumbs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Link } from "react-router";

import { RobotoLinkHoverUnderline } from "@/shared/components";
import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import { Slider } from "@/shared/foundation/primitives";

import styles from "./FileBrowserBreadcrumbs.module.css";
import { CRUMB_DROP_PREFIX } from "./FileBrowserDndContext";
import { useInheritedFiles } from "./inheritedFiles";
import { TriggerEvaluationStatus } from "./TriggerEvaluationStatus";

/** A breadcrumb that links to a page. */
export interface Crumb {
  label: string;
  to: string;
}

interface FileBrowserBreadcrumbsProps {
  /**
   * Links shown before the root folder's crumb, such as "Datasets"; files
   * cannot be dropped on them.
   */
  parentCrumbs: Crumb[];
  rootLabel: string;
  /** URL that opens the folder at a path; the root folder's path is empty. */
  folderUrl: (folderPath: string) => string;
  filePaths: string | undefined;
}

interface FileBrowserViewControlsProps {
  associationId: string;
  displayMode: string;
  gridItems: number;
  handleDisplayToggle: (mode: string) => void;
  handleGridSliderChange: (gridItems: number) => void;
  sliderValueFormat: (value: number) => string;
}

interface DroppableCrumbProps {
  label: string;
  to: string;
  dropId: string;
  currentPath: string;
}

const DroppableCrumb: React.FC<DroppableCrumbProps> = ({
  label,
  to,
  dropId,
  currentPath,
}) => {
  const crumbPath = dropId.slice(CRUMB_DROP_PREFIX.length);
  const isCurrentDir = crumbPath === currentPath;

  const { isOver, setNodeRef } = useDroppable({
    id: dropId,
    disabled: isCurrentDir,
  });

  return (
    <Tooltip title="Move here" open={isOver} placement="bottom" arrow>
      <span
        ref={setNodeRef}
        className={`${styles.crumb}${isOver ? ` ${styles["crumb-hot"]}` : ""}`}
      >
        <RobotoLinkHoverUnderline variant="body2" to={to}>
          {label}
        </RobotoLinkHoverUnderline>
      </span>
    </Tooltip>
  );
};

/**
 * The path to the open folder, from any parent crumbs down. Renders nothing at
 * the root folder of a browser with no parent crumbs, where the only crumb
 * would link to the page it is on.
 */
export const FileBrowserBreadcrumbs: React.FC<FileBrowserBreadcrumbsProps> = ({
  parentCrumbs,
  rootLabel,
  folderUrl,
  filePaths = "",
}) => {
  const theme = useTheme();

  const files =
    filePaths.length > 0
      ? filePaths.split("/").filter((item) => item.length > 0)
      : [];

  const currentPath = filePaths;

  if (files.length === 0 && parentCrumbs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: theme.spacing(0.5),
        alignItems: "center",
      }}
    >
      <Breadcrumbs
        maxItems={4}
        itemsAfterCollapse={2}
        itemsBeforeCollapse={1}
        separator={<NavigateNextIcon fontSize="small" />}
      >
        {parentCrumbs.map((crumb) => (
          <Link key={crumb.to} to={crumb.to} style={{ textDecoration: "none" }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                ":hover": { textDecoration: "underline" },
              }}
            >
              {crumb.label}
            </Typography>
          </Link>
        ))}

        {/* Root folder crumb; dropping items on it moves them to the root
              folder. `folderUrl("")` is the browser's own route (for a
              dataset, its `files` route, not the bare dataset URL), because
              passing through a redirecting index route remounts the browser
              and drops the current file selection. */}
        <DroppableCrumb
          label={rootLabel}
          to={folderUrl("")}
          dropId={`${CRUMB_DROP_PREFIX}`}
          currentPath={currentPath}
        />

        {files.map((item, index, filePathArray) => {
          const crumbPath = filePathArray.slice(0, index + 1).join("/");
          return (
            <DroppableCrumb
              key={crumbPath}
              label={item}
              to={folderUrl(crumbPath)}
              dropId={`${CRUMB_DROP_PREFIX}${crumbPath}`}
              currentPath={currentPath}
            />
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

/**
 * The list and grid toggle, the grid's zoom and, in a dataset, the status of
 * its triggers. Sits at the right end of the toolbar row.
 */
export const FileBrowserViewControls: React.FC<
  FileBrowserViewControlsProps
> = ({
  associationId,
  displayMode,
  gridItems,
  handleDisplayToggle,
  handleGridSliderChange,
  sliderValueFormat,
}) => {
  const theme = useTheme();
  const isInherited = useInheritedFiles() !== null;

  return (
    <Box
      sx={{
        gap: theme.spacing(1),
        cursor: "pointer",
        display: {
          xs: "none",
          sm: "flex",
        },
        alignItems: "center",
      }}
    >
      {fileAssociationType(associationId) === AssociationType.Dataset && (
        <TriggerEvaluationStatus
          className={styles["trigger-evaluation-status"]}
          datasetId={associationId}
        />
      )}
      {/* Inherited files stay in the table view, the only view that marks
            the ones the device overrides, so they get no view controls. */}
      {!isInherited && (
        <Box sx={{ display: "flex", gap: theme.spacing(1) }}>
          {displayMode === "grid" && (
            <div className={styles.zoomSlider}>
              <Slider
                value={gridItems}
                min={1}
                step={1}
                max={12}
                onValueChange={handleGridSliderChange}
                valueLabel={sliderValueFormat}
                aria-label="Grid zoom"
              />
            </div>
          )}

          <ToggleButtonGroup
            color="primary"
            exclusive
            onChange={(_, newView: string) => {
              handleDisplayToggle(newView);
            }}
            size="small"
            value={displayMode}
          >
            <ToggleButton value={"table"} aria-label="table-view">
              <TableRowsRoundedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={"grid"} aria-label="grid-view">
              <GridViewRoundedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  );
};
